import { readCookieValue } from '../utils/url-utils';
import {
  SERVER_ADDR_COOKIE_NAME,
  SESSION_TOKEN,
  XSRF_COOKIE_NAME,
  XSRF_HEADER_NAME
} from './api-consts';
import apiSessionStorage from './api-session-storage';

// check if server address is received in a cookie if if so, use it.
// this allows configuring server to live in a different address than where the client is loaded from, separate image f.ex.
const cookieServerAddr = readCookieValue(SERVER_ADDR_COOKIE_NAME);
if (cookieServerAddr) {
  console.log('Received server addr', cookieServerAddr);
}
const serverUrl = cookieServerAddr || process.env.REACT_APP_SERVER_URL;

interface ApiRequestOptions {
  storeSessionToken?: boolean;
  resetSessionToken?: boolean;
  excludeSessionToken?: boolean;
}

export interface ApiRequest extends RequestInit {
  body?: any;
  headers?: Headers;
  options?: ApiRequestOptions;
}

interface ApiError {
  message: string;
  details: string;
}

export interface ApiResult extends Body {
  ok: boolean;
  data?: any;
  message?: string;
  status?: string;
  details?: string;
  error?: ApiError;
}

interface ApiResponse extends Response {
  json(): Promise<ApiResult>;
  headers: Headers;
}

class ApiResponse extends Response implements ApiResponse {}

// TODO interface ApiHeaders
const getHeaders = (options: ApiRequestOptions | undefined): Headers => {
  const jwt = apiSessionStorage.getSessionToken();
  if (jwt) {
    if (!options || (options.resetSessionToken && !options.excludeSessionToken)) {
      return new Headers({
        'content-type': 'application/json',
        'session-token': jwt,
        [XSRF_HEADER_NAME]: readCookieValue(XSRF_COOKIE_NAME)
      });
    }
  }

  return new Headers({
    'content-type': 'application/json',
    [XSRF_HEADER_NAME]: readCookieValue(XSRF_COOKIE_NAME)
  });
};

const getUrl = (endpoint: string): RequestInfo => {
  return `${serverUrl}/api/${endpoint}`;
};

const apiFetch = (...args: Parameters<typeof fetch>): Promise<ApiResponse> => {
  return fetch.apply(window, args);
};

const postRequest = ({ method, options, body }: ApiRequest): ApiRequest => {
  return {
    headers: getHeaders(options),
    method,
    credentials: 'include',
    body: JSON.stringify(body)
  };
};

const getRequest = ({ options, method }: ApiRequest): ApiRequest => {
  return {
    headers: getHeaders(options),
    method,
    credentials: 'include'
  };
};

const putRequest = ({ method, options, body }: ApiRequest): ApiRequest => {
  return {
    headers: getHeaders(options),
    method,
    credentials: 'include',
    body: JSON.stringify(body)
  };
};

const deleteRequest = ({ method, options, body }: ApiRequest): ApiRequest => {
  return {
    headers: getHeaders(options),
    method,
    credentials: 'include',
    body: JSON.stringify(body)
  };
};

const decorateRequest = (request: ApiRequest): ApiRequest => {
  switch (request.method) {
    case 'GET':
      return getRequest(request);
    case 'PUT':
      return putRequest(request);
    case 'DELETE':
      return deleteRequest(request);
    default:
      return postRequest(request);
  }
};

const asyncCallApi = async (endpoint: string, request: ApiRequest): Promise<ApiResult> => {
  const requestInfo = getUrl(endpoint);
  const requestInit = decorateRequest(request);

  const response: ApiResponse = await apiFetch(requestInfo, requestInit);

  if ((request.options && request.options.resetSessionToken) || response.status === 401) {
    apiSessionStorage.destroySession();
  }

  if (request.options && request.options.storeSessionToken) {
    const jwt = response.headers.get(SESSION_TOKEN);
    if (jwt) apiSessionStorage.initSession(jwt);
  }

  const result = await response.json();

  if (!response.ok) {
    const body = {
      ok: false,
      error: {
        message: result.message || 'Something went wrong, please try again.',
        details: result.details || ''
      }
    };

    const errorResponse = new ApiResponse(JSON.stringify(body), { status: response.status });
    const errorResult = await errorResponse.json();

    return errorResult;
  }

  result.ok = response.ok;
  return result;
};

export default {
  asyncCallApi
};
